<template>
    <router-view />

    <portal-target
        name="overlays"
        multiple
    ></portal-target>

    <ProgressBar
        v-show="routeLoading"
        variant="indeterminate"
        class="fixed top-0 w-full h-0.5 z-70"
    />

    <FlashMessages />

    <TheAuthOverlay v-if="activeAuthOverlay" />

    <GlobalEvents
        v-if="globalListenersEnabled"
        @click.capture="globalClickEvent"
    ></GlobalEvents>
</template>

<script lang="ts">
import { defineAsyncComponent, defineComponent, provide } from 'vue';
import { GlobalEvents } from 'vue-global-events';
import { mapGetters } from 'vuex';
import FlashMessages from '@/components/FlashMessages';
import ModalUpdate from '@/components/ModalUpdate';
import ProgressBar from '@/components/ProgressBar';
import { provideIsDebugModeKey } from '@/components/keys';
import { findLinkInEvent } from '@/helpers/event';
import { isFirefox } from '@/helpers/browser';
import { pushGtmEvent, GtmEventName } from '@/utils/gtmUtils';
import { clickEvent } from '@/modules/globalEvents';
import { useRouteLoading } from '@/composables/routeLoading';
import { EventBus, EventBusEvent } from '@/plugins/eventBus';
import { trackEvent } from '@/modules/tracking';
import { useUserStore } from '@/stores/user';
import { mapActions } from 'pinia';

export default defineComponent({
    name: 'App',
    components: {
        FlashMessages,
        GlobalEvents,
        ProgressBar,
        TheAuthOverlay: defineAsyncComponent(() => import('@/components/TheAuthOverlay')),
    },
    setup() {
        const { loading: routeLoading } = useRouteLoading();
        const userStore = useUserStore();
        provide(provideIsDebugModeKey, import.meta.env.MIX_DEBUG === 'true');

        return {
            updateDismissed: false,
            updatePrompted: false,
            routeLoading,
            userStore,
        };
    },
    computed: {
        ...mapGetters('ui', [
            'globalListenersEnabled',
            'activeAuthOverlay',
        ]),
    },
    created() {
        // for click Events
        window.addEventListener('click', this.onClickCapture, true);
        // for contextmenu Events
        window.addEventListener('contextmenu', this.onClickCapture, true);
        // track auth refresh token failed
        EventBus.on(EventBusEvent.CLEAR_USER_DATA, () => this.userStore.logout());
        EventBus.on(EventBusEvent.TRACK_TOKEN_REFRESH_FAILED, this.trackAuthTokenRefreshFailed);
        EventBus.on(EventBusEvent.PROMPT_UPDATE, this.promptUpdate);
    },
    beforeUnmount() {
        window.removeEventListener('click', this.onClickCapture, { capture: true });
        window.removeEventListener('contextmenu', this.onClickCapture, { capture: true });
        EventBus.off(EventBusEvent.TRACK_TOKEN_REFRESH_FAILED, this.trackAuthTokenRefreshFailed);
        EventBus.off(EventBusEvent.CLEAR_USER_DATA, () => this.userStore.logout());
        EventBus.off(EventBusEvent.PROMPT_UPDATE, this.promptUpdate);
    },
    methods: {
        ...mapActions(useUserStore, ['loadUserData']),
        onClickCapture(e: MouseEvent) {
            const link = findLinkInEvent(e);
            if (!link) return;

            this.onLinkClick(link);
        },
        onLinkClick(link: HTMLLinkElement) {
            const url = new URL(link.href, window.location.origin);
            if (url.hostname.includes('studydrive')) {
                // the user stays on studydrive pages, so don't bother
                return;
            }

            if (link.target === '_blank') {
                // make sure that all the external link click on our page dont have: opener referrer (for privacy reasons)
                // eslint-disable-next-line no-param-reassign
                link.rel = isFirefox ? 'noopener noreferrer' : 'noopener';
            }

            pushGtmEvent(GtmEventName.LINK_OUT);
        },
        globalClickEvent(e: MouseEvent) {
            clickEvent(e, this.$store, this.$i18next);
        },
        trackAuthTokenRefreshFailed() {
            trackEvent('auth', 'refresh_token_failed');
        },
        promptUpdate() {
            if (this.updateDismissed || this.updatePrompted) return;

            this.$modal.open(ModalUpdate).then(() => {
                this.updateDismissed = true;
            });
            this.updatePrompted = true;
        },
    },
});
</script>

<style scoped lang="scss"></style>
